/* ────────────────────────────────────────────────────────────────────────
 * useContactActions — single source of truth for Call / Email actions
 * across the CRM (Customer Profile, Lead View, Inquiry Drawer, Quick
 * Action bars, CRM cards, Timeline quick actions, etc.).
 *
 * Two integration patterns are supported:
 *
 *   A. **Local mount** — call `useContactActions()` directly in the
 *      component that needs it. The hook owns the modal state and
 *      returns `modalElement` to render. Fine for surfaces that don't
 *      re-render their subtree (e.g. Customer Profile header).
 *
 *   B. **Page-level provider** — wrap a page in
 *      `<ContactActionsProvider>` and have descendants call
 *      `useContactActions()` without rendering `modalElement`. The
 *      Provider mounts ONE modal at the page root that survives
 *      re-renders / unmounts of child rows. This is the correct
 *      pattern when the hook would otherwise live inside a list cell
 *      (the Inquiries table's NextActionCell unmounts every time the
 *      poll-triggered refetch flips `loading=true` and replaces rows
 *      with a Skeleton — taking a modal-mid-compose with it).
 *
 * Pattern B falls back to Pattern A automatically when no Provider is
 * in tree, so existing call sites work unchanged.
 *
 * Centralized rules either pattern enforces:
 *   • Call → `tel:` URI on a stripped-digits phone, snackbar when blank
 *   • Email → opens the canonical EmailHub ComposeModal pre-filled with
 *     the recipient. NEVER hands off to the OS mail client (no mailto:).
 *   • Disabled state + tooltip explain WHY a button can't be clicked.
 *   • Loading state on Email while the From-account list is fetched.
 * ──────────────────────────────────────────────────────────────────── */

import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import ComposeModal from "../../Pages/EmailHub/ComposeModal";
import { fetchAccounts, EmailAccount } from "../../Pages/EmailHub/emailHubApi";
import { useNotifier } from "../../Core/Notifier";

/** Strip everything that isn't a digit. tel: prefers a plain digit run
 *  on every OS dialer; "(647) 667-1647" works but the cleaner form is
 *  more reliable on Android + iOS. */
function stripPhoneDigits(raw: string): string {
  return (raw || "").replace(/\D+/g, "");
}

export interface EmailActionParams {
  /** Recipient email — required. If missing, the action shows a snackbar. */
  email?: string | null;
  /** Customer / lead name — used for the modal header context. */
  name?: string | null;
  /** Optional email thread to thread the reply onto. Most surfaces will
   *  leave this undefined; lead-aware surfaces can pass it in so the
   *  send is attributed to the existing thread. */
  threadId?: number | null;
  /** Pre-filled body (rich-text HTML allowed). The shared composer
   *  toolbar can still be used to overlay a template on top. */
  initialBody?: string;
  /** CRM linkage — forwarded onto the persisted email row so the
   *  Client Profile + Lead View "Email History" lists are single
   *  indexed queries. Either / both can be omitted: the backend will
   *  derive customer_id from lead.customer_id when only leadId is
   *  given, and from thread.customer_id when only threadId is given. */
  customerId?: number | null;
  leadId?: number | null;
  /** Fires after a successful send. Use to refresh local activity
   *  state on the host surface. */
  onSent?: () => void;
}

export interface ContactActionsApi {
  /** Trigger the phone dialer. Pass any format — we normalize internally. */
  handleCallAction: (phone?: string | null) => void;
  /** Open the canonical email compose modal pre-filled to this recipient. */
  handleEmailAction: (params: EmailActionParams) => void;
  /** True while the first accounts-fetch is in flight. Disables the
   *  Email button so users can't double-click into nothing. */
  emailLoading: boolean;
  /** Mount this once at the page root so the modal can render. When a
   *  `<ContactActionsProvider>` is in tree the hook returns `null`
   *  here — the Provider mounts the modal itself. */
  modalElement: React.ReactElement | null;
}

/* ── Provider context ────────────────────────────────────────────────
   Set by `<ContactActionsProvider>`. When present, the public hook
   returns the Provider's handlers (and skips its own modal mount)
   instead of allocating its own state. */
interface ContactActionsContextValue {
  handleCallAction:  ContactActionsApi["handleCallAction"];
  handleEmailAction: ContactActionsApi["handleEmailAction"];
  emailLoading:      boolean;
}
const ContactActionsContext = createContext<ContactActionsContextValue | null>(null);

/* ── Internal: the actual state owner ────────────────────────────────
   Used by both the standalone hook (Pattern A) and the Provider
   (Pattern B). Returns the API plus a `modalElement` that the caller
   must render somewhere in the tree. */
function useContactActionsState(): ContactActionsApi {
  const { showMessage, showErrorMessage } = useNotifier();

  const [accounts, setAccounts]   = useState<EmailAccount[]>([]);
  const [emailLoading, setLoading] = useState(false);
  // Accounts are fetched lazily on the first Email click and cached for
  // subsequent clicks — there's no reason to refetch them between sends.
  const accountsLoaded = useRef(false);

  const [modalOpen, setModalOpen]       = useState(false);
  const [pendingTo, setPendingTo]       = useState<string>("");
  const [pendingThread, setPendingThread] = useState<number | null>(null);
  const [pendingBody, setPendingBody]   = useState<string>("");
  const [pendingCustomerId, setPendingCustomerId] = useState<number | null>(null);
  const [pendingLeadId,     setPendingLeadId]     = useState<number | null>(null);
  const [pendingOnSent, setPendingOnSent] = useState<(() => void) | null>(null);

  const handleCallAction = useCallback((phone?: string | null) => {
    const digits = stripPhoneDigits(phone || "");
    if (!digits) {
      showErrorMessage("Phone number not available");
      return;
    }
    // Use a tel: URI — the OS dialer is the right surface for this
    // (works on desktop softphones, iOS/Android, click-to-call browser
    // extensions). location.href is the historically-portable trigger.
    window.location.href = `tel:${digits}`;
  }, [showErrorMessage]);

  const handleEmailAction = useCallback(async (params: EmailActionParams) => {
    const to = (params.email || "").trim();
    if (!to) {
      showErrorMessage("No email address available");
      return;
    }

    // Lazily load the From-account list. Cache after first success so
    // subsequent Email clicks open the modal instantly.
    if (!accountsLoaded.current) {
      setLoading(true);
      try {
        const list = await fetchAccounts();
        setAccounts(list);
        accountsLoaded.current = true;
        if (!list || list.length === 0) {
          showErrorMessage("No email accounts configured — connect one in Email Hub settings first.");
          setLoading(false);
          return;
        }
      } catch {
        showErrorMessage("Could not load email accounts. Try again.");
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    setPendingTo(to);
    setPendingThread(params.threadId ?? null);
    setPendingBody(params.initialBody ?? "");
    setPendingCustomerId(params.customerId ?? null);
    setPendingLeadId(params.leadId ?? null);
    // Wrap the caller's onSent in a stable closure that ALSO toasts
    // success — every Email send across the app gets a consistent
    // confirmation without each call site duplicating the snackbar.
    setPendingOnSent(() => () => {
      showMessage("Email sent");
      setModalOpen(false);
      params.onSent?.();
    });
    setModalOpen(true);
  }, [showErrorMessage, showMessage]);

  // Phantom span — see ContactActions doc-comment. Catches the React
  // synthetic-event bubble that escapes the portalled Dialog so a
  // click inside the modal can't fire ancestor handlers (TableRow
  // openThread, accordion toggle, etc.). `display: contents` keeps it
  // layout-neutral.
  const modalElement = (
    <span
      style={{ display: "contents" }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <ComposeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        accounts={accounts}
        replyTo={null}
        threadId={pendingThread}
        initialTo={pendingTo}
        initialBody={pendingBody}
        customerId={pendingCustomerId}
        leadId={pendingLeadId}
        onSent={() => pendingOnSent?.()}
      />
    </span>
  );

  return { handleCallAction, handleEmailAction, emailLoading, modalElement };
}

/**
 * Provider — mounts ONE modal at the page root and exposes the
 * handlers to every descendant via context. Wrap pages whose
 * descendants live inside re-rendering subtrees (table rows,
 * virtualised lists, drawer contents) so the modal isn't destroyed
 * when those subtrees re-render.
 */
export const ContactActionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const api = useContactActionsState();
  return (
    <ContactActionsContext.Provider value={{
      handleCallAction:  api.handleCallAction,
      handleEmailAction: api.handleEmailAction,
      emailLoading:      api.emailLoading,
    }}>
      {children}
      {api.modalElement}
    </ContactActionsContext.Provider>
  );
};

/**
 * Public hook. Returns the page-level Provider's API when wrapped;
 * otherwise mounts its own state (the caller renders `modalElement`).
 *
 * Callers always destructure the same four keys — `modalElement` is
 * just `null` when a Provider is in tree, so existing render code
 * keeps working without conditionals.
 */
export function useContactActions(): ContactActionsApi {
  const ctx = useContext(ContactActionsContext);
  // Both branches must be called unconditionally to keep the hooks
  // order stable across renders. The standalone state hook is cheap
  // when unused; we just don't render its modal.
  const local = useContactActionsState();
  if (ctx) {
    return {
      handleCallAction:  ctx.handleCallAction,
      handleEmailAction: ctx.handleEmailAction,
      emailLoading:      ctx.emailLoading,
      modalElement:      null,
    };
  }
  return local;
}
